import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, ButtonProps, CircularProgress } from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';

export type ClipboardPasteButtonProps = Omit<ButtonProps, 'onPaste' | 'onError'> & {
  label: string;
  inProgress: boolean;
  onPaste: (file: File) => void;
  onPasteError?: (message: string) => void;
};

export function ClipboardPasteButton({
  label,
  inProgress,
  fullWidth,
  onPaste,
  onPasteError,
  disabled,
  ...buttonProps
}: ClipboardPasteButtonProps) {
  const [isListening, setIsListening] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handlePaste = useCallback(
    async (event: ClipboardEvent) => {
      event.preventDefault();

      const items = event.clipboardData?.items;
      if (!items) {
        onPasteError?.('Unable to access clipboard data');
        return;
      }

      // Find the first image item in the clipboard
      let imageItem: DataTransferItem | null = null;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.includes('image')) {
          imageItem = items[i];
          break;
        }
      }

      if (!imageItem) {
        onPasteError?.('No image found in clipboard');
        return;
      }

      const blob = imageItem.getAsFile();
      if (!blob) {
        onPasteError?.('Could not read image from clipboard');
        return;
      }

      // Create a File object from the blob
      const timestamp = new Date().getTime();
      const extension = blob.type.split('/')[1] || 'png';
      const file = new File([blob], `clipboard-image-${timestamp}.${extension}`, { type: blob.type });

      onPaste(file);
    },
    [onPaste, onPasteError],
  );

  const handleButtonClick = useCallback(async () => {
    try {
      // Try to read from clipboard API if available
      if (navigator.clipboard && navigator.clipboard.read) {
        const clipboardItems = await navigator.clipboard.read();

        for (const clipboardItem of clipboardItems) {
          for (const type of clipboardItem.types) {
            if (type.startsWith('image/')) {
              const blob = await clipboardItem.getType(type);
              const timestamp = new Date().getTime();
              const extension = type.split('/')[1] || 'png';
              const file = new File([blob], `clipboard-image-${timestamp}.${extension}`, { type });
              onPaste(file);
              return;
            }
          }
        }

        onPasteError?.('No image found in clipboard');
      } else {
        // Fallback: show message to use keyboard shortcut
        onPasteError?.('Please use Ctrl+V (or Cmd+V on Mac) to paste from clipboard');
      }
    } catch (error) {
      // If permission denied or not supported, instruct user to use keyboard
      onPasteError?.('Please use Ctrl+V (or Cmd+V on Mac) to paste from clipboard');
    }
  }, [onPaste, onPasteError]);

  // Set up global paste event listener when button is focused
  useEffect(() => {
    const handleGlobalPaste = (event: ClipboardEvent) => {
      if (isListening && !disabled && !inProgress) {
        handlePaste(event);
      }
    };

    if (isListening) {
      document.addEventListener('paste', handleGlobalPaste);
      return () => {
        document.removeEventListener('paste', handleGlobalPaste);
      };
    }
  }, [isListening, disabled, inProgress, handlePaste]);

  const handleFocus = useCallback(() => {
    setIsListening(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsListening(false);
  }, []);

  return (
    <Button
      ref={buttonRef}
      fullWidth={fullWidth}
      {...buttonProps}
      disabled={inProgress || disabled}
      startIcon={inProgress ? <CircularProgress size={20} /> : <ContentPasteIcon />}
      onClick={handleButtonClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {label}
    </Button>
  );
}

export default ClipboardPasteButton;
